import React from 'react';
import { ScoreBreakdown } from '@/types/survey';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Check, AlertTriangle, Info, BarChart } from 'lucide-react';
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { useLanguage } from '@/contexts/LanguageContext';
import { 
  PieChart,
  Pie,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip
} from 'recharts';

interface ResultsChartProps {
  scoreBreakdown: ScoreBreakdown;
}

const ResultsChart: React.FC<ResultsChartProps> = ({ scoreBreakdown }) => {
  const { t } = useLanguage();
  
  const getRatingColor = (rating: string) => {
    switch (rating) {
      case 'High Risk':
        return 'text-red-600';
      case 'Competitive':
        return 'text-amber-600';
      case 'Exceptional':
        return 'text-green-600';
      default:
        return 'text-blue-600';
    }
  };

  const getEroiColor = (eroi: number) => {
    if (eroi < 1.0) return 'text-red-600';
    if (eroi < 2.0) return 'text-amber-600';
    return 'text-green-600';
  };

  const getEroiLabel = (eroi: number) => {
    if (eroi < 1.0) return t('chart.needsImprovement');
    if (eroi < 2.0) return t('chart.moderateReturn');
    return t('chart.strongReturn');
  };

  // Prepare data for pie chart
  const COLORS = ['#9b87f5', '#7E69AB', '#F97316', '#0EA5E9', '#8B5CF6'];
  const pieData = [
    { name: t('metrics.emotional'), value: scoreBreakdown.ee.percentage },
    { name: t('metrics.legacy'), value: scoreBreakdown.lq.percentage },
    { name: t('metrics.friction'), value: scoreBreakdown.fc.percentage },
    { name: t('metrics.trust'), value: scoreBreakdown.tb.percentage },
    { name: t('metrics.continuity'), value: scoreBreakdown.bc.percentage }
  ];

  // Custom renderer for the pie chart labels to ensure they appear inside the chart
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.7;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    
    return (
      <text 
        x={x} 
        y={y} 
        fill="#fff" 
        textAnchor="middle" 
        dominantBaseline="central"
        fontSize={12}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className="space-y-6">
      {/* Category Distribution Chart - now full width and larger */}
      <div className="bg-white p-5 rounded-lg border border-gray-200 mb-6">
        <h3 className="text-lg font-bold text-gray-700 mb-3">{t('results.categoryDistribution')}</h3>
        <div className="h-[350px]"> {/* Increased height for better visibility */}
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={70} 
                outerRadius={140} 
                fill="#8884d8"
                paddingAngle={3}
                dataKey="value"
                labelLine={false}
                label={renderCustomizedLabel}
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Legend 
                layout="horizontal" 
                verticalAlign="bottom" 
                align="center"
                wrapperStyle={{ padding: '20px 0 0', position: 'relative' }}
                formatter={(value) => <span style={{ fontSize: '14px', padding: '0 8px', color: '#333' }}>{value}</span>} 
              />
              <RechartsTooltip 
                formatter={(value) => {
                  if (typeof value === 'number') {
                    return [`${value.toFixed(2)}%`, 'Value'];
                  }
                  return ['', ''];
                }}
                contentStyle={{ fontSize: '12px', padding: '8px', borderRadius: '4px' }} 
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
          
      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('results.metricsBreakdown')}</h3>
        
        <div>
          <div className="flex justify-between mb-1">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-base font-medium text-purple-600 flex items-center cursor-help">
                    {t('metrics.emotional')} <Info className="ml-1 h-4 w-4 text-gray-400" />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="bg-white border-gray-200 text-gray-700">
                  <p>{t('methodology.ea.description')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <span className="text-base font-medium text-purple-600">
              {scoreBreakdown.ee.score.toFixed(1)}/{scoreBreakdown.ee.total}
            </span>
          </div>
          <Progress value={scoreBreakdown.ee.percentage} className="h-3 bg-gray-100" 
            indicatorClassName="bg-purple-500" />
          <p className="mt-1 text-sm text-gray-600">{scoreBreakdown.ee.feedback}</p>
        </div>
        
        <div>
          <div className="flex justify-between mb-1">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-base font-medium text-blue-600 flex items-center cursor-help">
                    {t('metrics.legacy')} <Info className="ml-1 h-4 w-4 text-gray-400" />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="bg-white border-gray-200 text-gray-700">
                  <p>{t('methodology.elv.description')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <span className="text-base font-medium text-blue-600">
              {scoreBreakdown.lq.score.toFixed(1)}/{scoreBreakdown.lq.total}
            </span>
          </div>
          <Progress value={scoreBreakdown.lq.percentage} className="h-3 bg-gray-100"
            indicatorClassName="bg-blue-500" />
          <p className="mt-1 text-sm text-gray-600">{scoreBreakdown.lq.feedback}</p>
        </div>
        
        <div>
          <div className="flex justify-between mb-1">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-base font-medium text-red-600 flex items-center cursor-help">
                    {t('metrics.friction')} <Info className="ml-1 h-4 w-4 text-gray-400" />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="bg-white border-gray-200 text-gray-700">
                  <p>{t('methodology.l.description')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <span className="text-base font-medium text-red-600">
              {scoreBreakdown.fc.score.toFixed(1)}/{scoreBreakdown.fc.total}
            </span>
          </div>
          <Progress value={scoreBreakdown.fc.percentage} className="h-3 bg-gray-100"
            indicatorClassName="bg-red-500" />
          <p className="mt-1 text-sm text-gray-600">{scoreBreakdown.fc.feedback}</p>
        </div>
        
        <div>
          <div className="flex justify-between mb-1">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-base font-medium text-green-600 flex items-center cursor-help">
                    {t('metrics.trust')} <Info className="ml-1 h-4 w-4 text-gray-400" />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="bg-white border-gray-200 text-gray-700">
                  <p>{t('methodology.ea.metric4')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <span className="text-base font-medium text-green-600">
              {scoreBreakdown.tb.score.toFixed(1)}/{scoreBreakdown.tb.total}
            </span>
          </div>
          <Progress value={scoreBreakdown.tb.percentage} className="h-3 bg-gray-100"
            indicatorClassName="bg-green-500" />
          <p className="mt-1 text-sm text-gray-600">{scoreBreakdown.tb.feedback}</p>
        </div>

        {scoreBreakdown.sentimentAnalysis && scoreBreakdown.sentimentAnalysis.detectedIssues.length > 0 && (
          <Alert className="mt-6 border-amber-300 bg-amber-50">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertTitle className="text-amber-700">{t('chart.sentimentAnalysis')}</AlertTitle>
            <AlertDescription className="text-amber-700">
              <ul className="list-disc pl-5 mt-2 space-y-1">
                {scoreBreakdown.sentimentAnalysis.detectedIssues.map((issue, i) => (
                  <li key={i}>{issue}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {scoreBreakdown.pprAlert && (
          <Alert className="mt-6 border-amber-300 bg-amber-50">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertTitle className="text-amber-700">{t('chart.frictionAlert')}</AlertTitle>
            <AlertDescription className="text-amber-700">
              {scoreBreakdown.pprAlert}
            </AlertDescription>
          </Alert>
        )}
        
        {scoreBreakdown.sentimentAnalysis && scoreBreakdown.sentimentAnalysis.dominantEmotions.length > 0 && (
          <div className="mt-6 p-4 bg-white rounded-lg border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center mb-2">
              <BarChart className="h-4 w-4 mr-2" /> {t('chart.dominantEmotions')}
            </h3>
            <div className="flex flex-wrap gap-2">
              {scoreBreakdown.sentimentAnalysis.dominantEmotions.map((emotion, i) => (
                <div key={i} className="px-3 py-1 bg-gray-50 rounded-full text-sm border border-gray-200 text-gray-700">
                  {emotion === 'joy' && '😊 '}
                  {emotion === 'trust' && '🤝 '}
                  {emotion === 'frustration' && '😣 '}
                  {emotion === 'indifference' && '😐 '}
                  {emotion.charAt(0).toUpperCase() + emotion.slice(1)}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      <div className="mt-8 border-t border-gray-200 pt-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">{t('results.topRecommendations')}</h3>
        
        <div className="space-y-4">
          {scoreBreakdown.recommendations.slice(0, 3).map((recommendation, index) => {
            // Try to find a translation for this recommendation
            const translationKey = `recommendations.${recommendation.toLowerCase().replace(/\s+/g, '')}`;
            const translatedRecommendation = t(translationKey);
            
            // Define priority levels and colors
            const priorityLevel = index === 0 ? 'high' : index === 1 ? 'medium' : 'standard';
            const priorityColor = index === 0 ? 'bg-red-50 border-red-200' : 
                                index === 1 ? 'bg-amber-50 border-amber-200' : 
                                'bg-blue-50 border-blue-200';
            const iconColor = index === 0 ? 'text-red-600 bg-red-100' : 
                           index === 1 ? 'text-amber-600 bg-amber-100' : 
                           'text-blue-600 bg-blue-100';
            
            return (
              <div key={index} className={`p-3 rounded-lg border ${priorityColor}`}>
                <div className="flex items-start gap-3">
                  <div className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${iconColor}`}>
                    {index + 1}
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">
                      {translatedRecommendation !== translationKey 
                        ? translatedRecommendation 
                        : recommendation}
                    </p>
                    <p className="text-xs mt-1 text-gray-600">
                      {priorityLevel === 'high' ? t('results.recommendationHighPriority') : 
                       priorityLevel === 'medium' ? t('results.recommendationMediumPriority') : 
                       t('results.recommendationLowPriority')}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ResultsChart;
